import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X, Search, Sun, Moon, Briefcase, Layers, Newspaper, Users, Building2, Shirt, Plane, Car, Film, Smartphone, Landmark, BookOpen, HelpCircle, Scale } from 'lucide-react';
import SearchModal from './SearchModal';
import { useTheme } from './theme-provider';
import { fetchBrand, getBrandLogo } from '@/lib/cms';

// Wrap any image URL with Cloudflare Image Resizing.
// Uses a relative path so it always matches the current origin (www.adventive.com or adventive.com).
function cfImg(src: string, options: string): string {
  return `/cdn-cgi/image/${options}/${src}`;
}

const CF_LOGO_OPTS = 'height=40,format=auto,quality=90';

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());
  const [logoSrc, setLogoSrc] = useState('https://media.adventive.com/brand/logos/wordmark/png/adventive_wordmark-black.png');
  const [logoWhiteSrc, setLogoWhiteSrc] = useState('https://media.adventive.com/brand/logos/wordmark/png/adventive_wordmark-white.png');
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  // Refs for dropdown trigger buttons (to restore focus on close)
  const dropdownTriggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const solutions = [
    { name: 'Publishers', href: '/solutions/publishers', icon: Newspaper, description: 'Monetize your content' },
    { name: 'Agencies', href: '/solutions/agencies', icon: Users, description: 'Scale client campaigns' },
    { name: 'Brands', href: '/solutions/brands', icon: Building2, description: 'Drive direct engagement' },
  ];

  const industries = [
    { name: 'Fashion', href: '/solutions/fashion', icon: Shirt, description: 'Style meets performance' },
    { name: 'Travel & Tourism', href: '/solutions/travel-and-tourism', icon: Plane, description: 'Inspire wanderlust' },
    { name: 'Automotive', href: '/solutions/automotive', icon: Car, description: 'Drive conversions' },
    { name: 'Entertainment & Media', href: '/solutions/entertainment-and-media', icon: Film, description: 'Captivate audiences' },
    { name: 'Consumer Electronics', href: '/solutions/consumer-electronics', icon: Smartphone, description: 'Showcase innovation' },
    { name: 'Financial Services', href: '/solutions/financial-services', icon: Landmark, description: 'Build trust & engagement' },
  ];

  const platform = [
    { name: 'Platform Overview', href: '/platform', icon: Layers, description: 'See the full platform' },
    { name: 'Managed Services', href: '/managed-services', icon: Briefcase, description: 'Done-for-you ad creation' },
  ];

  const resources = [
    { name: 'All Resources', href: '/resources', icon: BookOpen, description: 'Guides, tips & insights' },
    { name: 'Blog', href: '/blog', icon: Newspaper, description: 'Latest news & articles' },
    { name: 'Help Center', href: 'https://help.adventive.com', icon: HelpCircle, description: 'Get support & answers', target: '_blank' },
    { name: 'Legal', href: '/resources/legal', icon: Scale, description: 'Terms, privacy & policies' },
    { name: 'About Adventive', href: '/about', icon: Users, description: 'Our story & mission' },
  ];


  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchBrand().then(brand => {
      if (!brand?.logos?.length) return;
      const dark = getBrandLogo(brand.logos, 'wordmark', 'black', 'png');
      const white = getBrandLogo(brand.logos, 'wordmark', 'white', 'png');
      if (dark) setLogoSrc(dark);
      if (white) setLogoWhiteSrc(white);
    }).catch(() => { /* keep defaults */ });
  }, []);

  const handleDropdownEnter = (dropdown: string) => {
    setActiveDropdown(dropdown);
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };

  const closeDropdown = useCallback((key: string) => {
    setActiveDropdown(null);
    dropdownTriggerRefs.current[key]?.focus();
  }, []);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const toggleAccordion = (section: string) => {
    setOpenAccordions((prev) => {
      if (prev.has(section)) {
        const newSet = new Set(prev);
        newSet.delete(section);
        return newSet;
      }
      return new Set([section]);
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isDark = theme === 'dark';

  // Handle keyboard navigation inside a dropdown menu
  const handleDropdownKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>, key: string) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown(key);
      return;
    }
    const items = e.currentTarget.querySelectorAll<HTMLElement>('a, button');
    const arr = Array.from(items);
    const idx = arr.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      arr[(idx + 1) % arr.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      arr[(idx - 1 + arr.length) % arr.length]?.focus();
    }
  }, [closeDropdown]);

  // Open dropdown and move focus to first item on keyboard trigger
  const handleTriggerKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, key: string) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveDropdown(key);
      // Focus first item after render
      setTimeout(() => {
        const menu = document.querySelector<HTMLElement>(`[data-dropdown="${key}"] a, [data-dropdown="${key}"] button`);
        menu?.focus();
      }, 50);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setActiveDropdown(null);
    }
  }, []);

  // Reusable dropdown item component
  const DropdownItem = ({ item }: { item: { name: string; href: string; icon: React.ElementType; description: string; target?: string } }) => {
    const className = "flex items-start gap-3 px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-[#635bff] dark:hover:text-[#635bff] transition-colors";
    const content = (
      <>
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#635bff]/10 to-[#7c3aed]/10 dark:from-[#635bff]/20 dark:to-[#7c3aed]/20 flex items-center justify-center flex-shrink-0">
          <item.icon className="w-4 h-4 text-[#635bff]" />
        </div>
        <div>
          <p className="font-medium">{item.name}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{item.description}</p>
        </div>
      </>
    );
    return item.target === '_blank'
      ? <a href={item.href} target="_blank" rel="noopener noreferrer" className={className} role="menuitem">{content}</a>
      : <Link to={item.href} className={className} role="menuitem">{content}</Link>;
  };

  return (
    <>
      {/* Skip navigation — visible on focus for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[#635bff] focus:text-white focus:rounded-lg focus:text-sm focus:font-semibold"
      >
        Skip to main content
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50'
            : 'bg-transparent'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo — served via Cloudflare Image Resizing at 2x (40px) for retina, rendered at h-5 (20px) via CSS */}
            <Link to="/" className="flex items-center">
              <img
                src={cfImg(logoSrc, CF_LOGO_OPTS)}
                alt="Adventive"
                width={324}
                height={40}
                className="header-logo h-5 w-auto dark:hidden"
              />
              <img
                src={cfImg(logoWhiteSrc, CF_LOGO_OPTS)}
                alt="Adventive"
                width={324}
                height={40}
                className="header-logo h-5 w-auto hidden dark:block"
              />
            </Link>



            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {/* Solutions Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownEnter('solutions')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  ref={el => { dropdownTriggerRefs.current['solutions'] = el; }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
                  aria-expanded={activeDropdown === 'solutions'}
                  aria-haspopup="true"
                  onKeyDown={e => handleTriggerKeyDown(e, 'solutions')}
                >
                  Solutions
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'solutions' ? 'rotate-180' : ''}`} />
                </button>
                <div
                  data-dropdown="solutions"
                  role="menu"
                  aria-label="Solutions menu"
                  onKeyDown={e => handleDropdownKeyDown(e, 'solutions')}
                  className={`absolute top-full left-0 w-72 pt-2 transition-all duration-200 ${
                    activeDropdown === 'solutions' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
                  }`}
                >
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl shadow-slate-200/50 dark:shadow-black/30 border border-slate-200/50 dark:border-slate-700/50 py-2 overflow-hidden">
                    {solutions.map((item) => (
                      <DropdownItem key={item.name} item={item} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Industries Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownEnter('industries')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  ref={el => { dropdownTriggerRefs.current['industries'] = el; }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
                  aria-expanded={activeDropdown === 'industries'}
                  aria-haspopup="true"
                  onKeyDown={e => handleTriggerKeyDown(e, 'industries')}
                >
                  Industries
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'industries' ? 'rotate-180' : ''}`} />
                </button>
                <div
                  data-dropdown="industries"
                  role="menu"
                  aria-label="Industries menu"
                  onKeyDown={e => handleDropdownKeyDown(e, 'industries')}
                  className={`absolute top-full left-0 w-72 pt-2 transition-all duration-200 ${
                    activeDropdown === 'industries' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
                  }`}
                >
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl shadow-slate-200/50 dark:shadow-black/30 border border-slate-200/50 dark:border-slate-700/50 py-2 overflow-hidden">
                    {industries.map((item) => (
                      <DropdownItem key={item.name} item={item} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Platform Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownEnter('platform')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  ref={el => { dropdownTriggerRefs.current['platform'] = el; }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
                  aria-expanded={activeDropdown === 'platform'}
                  aria-haspopup="true"
                  onKeyDown={e => handleTriggerKeyDown(e, 'platform')}
                >
                  Platform
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'platform' ? 'rotate-180' : ''}`} />
                </button>
                <div
                  data-dropdown="platform"
                  role="menu"
                  aria-label="Platform menu"
                  onKeyDown={e => handleDropdownKeyDown(e, 'platform')}
                  className={`absolute top-full left-0 w-72 pt-2 transition-all duration-200 ${
                    activeDropdown === 'platform' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
                  }`}
                >
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl shadow-slate-200/50 dark:shadow-black/30 border border-slate-200/50 dark:border-slate-700/50 py-2 overflow-hidden">
                    {platform.map((item) => (
                      <DropdownItem key={item.name} item={item} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Resources Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownEnter('resources')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  ref={el => { dropdownTriggerRefs.current['resources'] = el; }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
                  aria-expanded={activeDropdown === 'resources'}
                  aria-haspopup="true"
                  onKeyDown={e => handleTriggerKeyDown(e, 'resources')}
                >
                  Resources
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'resources' ? 'rotate-180' : ''}`} />
                </button>
                <div
                  data-dropdown="resources"
                  role="menu"
                  aria-label="Resources menu"
                  onKeyDown={e => handleDropdownKeyDown(e, 'resources')}
                  className={`absolute top-full left-0 w-72 pt-2 transition-all duration-200 ${
                    activeDropdown === 'resources' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
                  }`}
                >
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl shadow-slate-200/50 dark:shadow-black/30 border border-slate-200/50 dark:border-slate-700/50 py-2 overflow-hidden">
                    {resources.map((item) => (
                      <DropdownItem key={item.name} item={item} />
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to="/gallery"
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                Gallery
              </Link>
            </div>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center space-x-3">
              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-slate-100/80 dark:bg-slate-800/80 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 rounded-lg transition-all"
                aria-label="Search (⌘K)"
              >
                <Search className="h-4 w-4" aria-hidden="true" />
                <span className="hidden xl:inline">Search</span>
                <kbd className="hidden xl:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-slate-500 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded" aria-hidden="true">
                  ⌘K
                </kbd>
              </button>

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-slate-100/80 dark:bg-slate-800/80 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 rounded-lg transition-all"
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
              </button>

              <a
                href="https://console.adventive.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors px-4 py-2"
              >
                Sign in
              </a>
              <button
                onClick={() => navigate('/demo')}
                className="stripe-button px-5 py-2.5 text-sm font-semibold text-white rounded-full"
              >
                Request Demo
              </button>
            </div>

            {/* Mobile buttons */}
            <div className="flex items-center gap-2 lg:hidden">
              <button
                onClick={toggleTheme}
                className="p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
              </button>
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                aria-label="Search"
              >
                <Search className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
              </button>
            </div>
          </div>


          {/* Mobile Menu */}
          <div
            id="mobile-menu"
            className={`lg:hidden fixed inset-x-0 top-16 h-[calc(100vh-4rem)] bg-white dark:bg-slate-900 overflow-y-auto transition-all duration-300 z-40 ${
              mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
            }`}
            aria-hidden={!mobileMenuOpen}
          >
            <div className="py-4 border-t border-slate-200/50 dark:border-slate-700/50">
              <div className="space-y-1">
                {/* Solutions Accordion */}
                <div className="border-b border-slate-200/50 dark:border-slate-700/50">
                  <button
                    onClick={() => toggleAccordion('solutions')}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    aria-expanded={openAccordions.has('solutions')}
                    aria-controls="mobile-solutions"
                  >
                    <span className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Solutions
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${
                        openAccordions.has('solutions') ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                  <div
                    id="mobile-solutions"
                    className={`overflow-hidden transition-all duration-300 ${
                      openAccordions.has('solutions') ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="pb-2">
                      {solutions.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-[#635bff]"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#635bff]/10 to-[#7c3aed]/10 dark:from-[#635bff]/20 dark:to-[#7c3aed]/20 flex items-center justify-center flex-shrink-0">
                            <item.icon className="w-4 h-4 text-[#635bff]" aria-hidden="true" />
                          </div>
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Industries Accordion */}
                <div className="border-b border-slate-200/50 dark:border-slate-700/50">
                  <button
                    onClick={() => toggleAccordion('industries')}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    aria-expanded={openAccordions.has('industries')}
                    aria-controls="mobile-industries"
                  >
                    <span className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Industries
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${
                        openAccordions.has('industries') ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                  <div
                    id="mobile-industries"
                    className={`overflow-hidden transition-all duration-300 ${
                      openAccordions.has('industries') ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="pb-2">
                      {industries.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-[#635bff]"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#635bff]/10 to-[#7c3aed]/10 dark:from-[#635bff]/20 dark:to-[#7c3aed]/20 flex items-center justify-center flex-shrink-0">
                            <item.icon className="w-4 h-4 text-[#635bff]" aria-hidden="true" />
                          </div>
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Platform Accordion */}
                <div className="border-b border-slate-200/50 dark:border-slate-700/50">
                  <button
                    onClick={() => toggleAccordion('platform')}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    aria-expanded={openAccordions.has('platform')}
                    aria-controls="mobile-platform"
                  >
                    <span className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Platform
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${
                        openAccordions.has('platform') ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                  <div
                    id="mobile-platform"
                    className={`overflow-hidden transition-all duration-300 ${
                      openAccordions.has('platform') ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="pb-2">
                      {platform.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-[#635bff]"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#635bff]/10 to-[#7c3aed]/10 dark:from-[#635bff]/20 dark:to-[#7c3aed]/20 flex items-center justify-center flex-shrink-0">
                            <item.icon className="w-4 h-4 text-[#635bff]" aria-hidden="true" />
                          </div>
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>


                {/* Resources Accordion */}
                <div className="border-b border-slate-200/50 dark:border-slate-700/50">
                  <button
                    onClick={() => toggleAccordion('resources')}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    aria-expanded={openAccordions.has('resources')}
                    aria-controls="mobile-resources"
                  >
                    <span className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Resources
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${
                        openAccordions.has('resources') ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                  <div
                    id="mobile-resources"
                    className={`overflow-hidden transition-all duration-300 ${
                      openAccordions.has('resources') ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="pb-2">
                      {resources.map((item) => {
                        const mobileClassName = "flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-[#635bff]";
                        const mobileContent = (
                          <>
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#635bff]/10 to-[#7c3aed]/10 dark:from-[#635bff]/20 dark:to-[#7c3aed]/20 flex items-center justify-center flex-shrink-0">
                              <item.icon className="w-4 h-4 text-[#635bff]" aria-hidden="true" />
                            </div>
                            <div>
                              <p className="font-medium">{item.name}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
                            </div>
                          </>
                        );
                        return item.target === '_blank'
                          ? <a key={item.name} href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className={mobileClassName}>{mobileContent}</a>
                          : <Link key={item.name} to={item.href} onClick={() => setMobileMenuOpen(false)} className={mobileClassName}>{mobileContent}</Link>;
                      })}
                    </div>
                  </div>
                </div>
                <Link
                  to="/gallery"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Gallery
                </Link>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200/50 dark:border-slate-700/50 space-y-2 px-4">
                <a
                  href="https://console.adventive.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full py-2.5 text-sm font-medium text-center text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-full hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Sign in
                </a>
                <button
                  onClick={() => {
                    navigate('/demo');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full py-2.5 text-sm font-semibold text-white stripe-button rounded-full"
                >
                  Request Demo
                </button>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Header;
